import { ColScaffold, RowScaffold, ScaffoldSelection } from "../interface";

/**
 * Anchor point for shift+click range selection, recording the type and
 * identifier(s) of the cell, row, or column where the selection started.
 */
export type SelectionAnchor =
  | { type: "cell"; rowId: string; colId: string }
  | { type: "row"; rowId: string }
  | { type: "col"; colId: string };

/**
 * Internal state managed by the scaffold reducer, covering animation tracking,
 * drag state, and transient resize preview values.
 */
export interface ScaffoldState<T> {
  /** Snapshot of columns from the previous render, used for reconciliation. */
  prevCols: ColScaffold<T>[];
  /** Snapshot of rows from the previous render, used for reconciliation. */
  prevRows: RowScaffold<T>[];
  /** Column ids that are currently animating in. */
  enteringColIds: Set<string>;
  /** Row ids that are currently animating in. */
  enteringRowIds: Set<string>;
  /** Columns that have been removed and are currently animating out. */
  leavingCols: ColScaffold<T>[];
  /** Rows that have been removed and are currently animating out. */
  leavingRows: RowScaffold<T>[];
  /** Whether a drag event is currently active on the document. */
  isDragging: boolean;
  /** Transient column width overrides active during a resize drag. */
  previewColWidths: Record<string, number>;
  /** Transient row height overrides active during a resize drag. */
  previewRowHeights: Record<string, number>;
  /** Current selection state managed by the reducer. */
  selection: ScaffoldSelection;
  /** Anchor point for shift+click range selection. */
  selectionAnchor: SelectionAnchor | null;
}

// Action type constants
export const SET_DRAGGING = "SET_DRAGGING";
export const COLS_CHANGED = "COLS_CHANGED";
export const ROWS_CHANGED = "ROWS_CHANGED";
export const COL_LEAVE_COMPLETE = "COL_LEAVE_COMPLETE";
export const COL_ENTER_COMPLETE = "COL_ENTER_COMPLETE";
export const ROW_LEAVE_COMPLETE = "ROW_LEAVE_COMPLETE";
export const ROW_ENTER_COMPLETE = "ROW_ENTER_COMPLETE";
export const PREVIEW_COL_WIDTH_CHANGE = "PREVIEW_COL_WIDTH_CHANGE";
export const PREVIEW_ROW_HEIGHT_CHANGE = "PREVIEW_ROW_HEIGHT_CHANGE";
export const CELL_CLICKED = "CELL_CLICKED";
export const ROW_CLICKED = "ROW_CLICKED";
export const COL_CLICKED = "COL_CLICKED";
export const CLICK_OUTSIDE = "CLICK_OUTSIDE";
export const SELECTION_CHANGED = "SELECTION_CHANGED";

/**
 * Union of all scaffold action operation names.
 */
export type ScaffoldOperation =
  | typeof SET_DRAGGING
  | typeof COLS_CHANGED
  | typeof ROWS_CHANGED
  | typeof COL_LEAVE_COMPLETE
  | typeof COL_ENTER_COMPLETE
  | typeof ROW_LEAVE_COMPLETE
  | typeof ROW_ENTER_COMPLETE
  | typeof PREVIEW_COL_WIDTH_CHANGE
  | typeof PREVIEW_ROW_HEIGHT_CHANGE
  | typeof CELL_CLICKED
  | typeof ROW_CLICKED
  | typeof COL_CLICKED
  | typeof CLICK_OUTSIDE
  | typeof SELECTION_CHANGED;

/**
 * Base interface for all scaffold actions.
 */
export interface IScaffoldActionBase {
  /** Discriminator for the reducer switch statement. */
  operation: ScaffoldOperation;
}

/**
 * Action to update the dragging state when a document drag event starts or ends.
 */
export interface IScaffoldActionSetDragging extends IScaffoldActionBase {
  operation: typeof SET_DRAGGING;
  /** Whether a drag is currently in progress. */
  isDragging: boolean;
}

/**
 * Action dispatched when the columns prop changes, triggering reconciliation.
 */
export interface IScaffoldActionColsChanged<T> extends IScaffoldActionBase {
  operation: typeof COLS_CHANGED;
  /** The new set of columns from the current render. */
  cols: ColScaffold<T>[];
}

/**
 * Action dispatched when the rows prop changes, triggering reconciliation.
 */
export interface IScaffoldActionRowsChanged<T> extends IScaffoldActionBase {
  operation: typeof ROWS_CHANGED;
  /** The new set of rows from the current render. */
  rows: RowScaffold<T>[];
}

/**
 * Action dispatched when a column has completed its exit animation.
 */
export interface IScaffoldActionColLeaveComplete extends IScaffoldActionBase {
  operation: typeof COL_LEAVE_COMPLETE;
  /** The id of the column that finished animating out. */
  colId: string;
}

/**
 * Action dispatched when a column has completed its entry animation.
 */
export interface IScaffoldActionColEnterComplete extends IScaffoldActionBase {
  operation: typeof COL_ENTER_COMPLETE;
  /** The id of the column that finished animating in. */
  colId: string;
}

/**
 * Action dispatched when a row has completed its exit animation.
 */
export interface IScaffoldActionRowLeaveComplete extends IScaffoldActionBase {
  operation: typeof ROW_LEAVE_COMPLETE;
  /** The id of the row that finished animating out. */
  rowId: string;
}

/**
 * Action dispatched when a row has completed its entry animation.
 */
export interface IScaffoldActionRowEnterComplete extends IScaffoldActionBase {
  operation: typeof ROW_ENTER_COMPLETE;
  /** The id of the row that finished animating in. */
  rowId: string;
}

/**
 * Action dispatched to store or clear a transient column width during a resize drag.
 */
export interface IScaffoldActionPreviewColWidthChange extends IScaffoldActionBase {
  operation: typeof PREVIEW_COL_WIDTH_CHANGE;
  /** The id of the column being resized. */
  colId: string;
  /** Width in pixels, or null to clear the transient override. */
  width: number | null;
}

/**
 * Action dispatched to store or clear a transient row height during a resize drag.
 */
export interface IScaffoldActionPreviewRowHeightChange extends IScaffoldActionBase {
  operation: typeof PREVIEW_ROW_HEIGHT_CHANGE;
  /** The id of the row being resized. */
  rowId: string;
  /** Height in pixels, or null to clear the transient override. */
  height: number | null;
}

/**
 * Action dispatched when a scaffold cell is clicked, carrying modifier key state
 * for ctrl/shift/meta selection behaviour.
 */
export interface IScaffoldActionCellClicked extends IScaffoldActionBase {
  operation: typeof CELL_CLICKED;
  /** The row ID of the clicked cell. */
  rowId: string;
  /** The column ID of the clicked cell. */
  colId: string;
  /** Whether the Ctrl key was held during the click. */
  ctrlKey: boolean;
  /** Whether the Meta (Cmd) key was held during the click. */
  metaKey: boolean;
  /** Whether the Shift key was held during the click. */
  shiftKey: boolean;
}

/**
 * Action dispatched when a row selection handle is clicked, carrying modifier key state
 * for ctrl/shift/meta selection behaviour.
 */
export interface IScaffoldActionRowClicked extends IScaffoldActionBase {
  operation: typeof ROW_CLICKED;
  /** The row ID of the clicked handle. */
  rowId: string;
  /** Whether the Ctrl key was held during the click. */
  ctrlKey: boolean;
  /** Whether the Meta (Cmd) key was held during the click. */
  metaKey: boolean;
  /** Whether the Shift key was held during the click. */
  shiftKey: boolean;
}

/**
 * Action dispatched when a column selection handle is clicked, carrying modifier key state
 * for ctrl/shift/meta selection behaviour.
 */
export interface IScaffoldActionColClicked extends IScaffoldActionBase {
  operation: typeof COL_CLICKED;
  /** The column ID of the clicked handle. */
  colId: string;
  /** Whether the Ctrl key was held during the click. */
  ctrlKey: boolean;
  /** Whether the Meta (Cmd) key was held during the click. */
  metaKey: boolean;
  /** Whether the Shift key was held during the click. */
  shiftKey: boolean;
}

/**
 * Action dispatched when the user clicks outside the scaffold table, clearing
 * any active selection.
 */
export interface IScaffoldActionClickOutside extends IScaffoldActionBase {
  operation: typeof CLICK_OUTSIDE;
}

/**
 * Action dispatched to sync an externally controlled selection value from props
 * into the reducer state.
 */
export interface IScaffoldActionSelectionChanged extends IScaffoldActionBase {
  operation: typeof SELECTION_CHANGED;
  /** The externally controlled selection value to sync from props. */
  selection: ScaffoldSelection;
}

/**
 * Union of all scaffold actions.
 */
export type IScaffoldAction<T> =
  | IScaffoldActionSetDragging
  | IScaffoldActionColsChanged<T>
  | IScaffoldActionRowsChanged<T>
  | IScaffoldActionColLeaveComplete
  | IScaffoldActionColEnterComplete
  | IScaffoldActionRowLeaveComplete
  | IScaffoldActionRowEnterComplete
  | IScaffoldActionPreviewColWidthChange
  | IScaffoldActionPreviewRowHeightChange
  | IScaffoldActionCellClicked
  | IScaffoldActionRowClicked
  | IScaffoldActionColClicked
  | IScaffoldActionClickOutside
  | IScaffoldActionSelectionChanged;
